import React, { useCallback, useMemo, useEffect, useContext } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { DatePickerOption, Locations } from 'tripPlanner/pages/trip/GenerateTripPage'
import moment from 'moment'
import AITripDatePicker from './AITripDatePicker'
import * as Analytics from 'analytics/analytics'
import Clickable from 'components/Common/Clickable'
import Sheet from 'components/utils/Sheet'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalContext from 'contexts/ModalContext'
import { formatCasualDateRange } from 'tripPlanner/utils'
import SortableAITripDateOrder from './SortableAITripDateOrder'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

const DateInput = styled(Sheet)`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  width: ${rem(328)};
  max-width: 100%;
`

interface Props {
  datePicker: DatePickerOption
  onDatePickerSelect: (datePicker: DatePickerOption) => void
  stepNumber: number
  totalSteps: number
  locations: Array<Locations>
  onLocationsReorder?: (locations: Array<Locations>) => void
  onLocationDurationChange?: (locationIndex: number, newDuration: number) => void
  onLocationDelete?: (locationIndex: number) => void
}

function AITripDatesSelection({
  datePicker,
  onDatePickerSelect,
  stepNumber,
  totalSteps,
  locations,
  onLocationsReorder,
  onLocationDurationChange,
  onLocationDelete,
}: Props) {
  const showModal = useContext(ModalContext)

  const initStartDate = useMemo(() =>
    datePicker.startDate ? moment(datePicker.startDate) : undefined,
  [datePicker.startDate],
  )

  const initEndDate = useMemo(() =>
    datePicker.endDate ? moment(datePicker.endDate) : undefined,
  [datePicker.endDate],
  )

  const hasSelectedDates = initStartDate && initEndDate

  const handleSetDates = useCallback((startDate?: moment.Moment, endDate?: moment.Moment) => {
    onDatePickerSelect({
      startDate: startDate ? new Date(startDate.format('YYYY-MM-DD')) : undefined,
      endDate: endDate ? new Date(endDate.format('YYYY-MM-DD')) : undefined,
    })
  }, [onDatePickerSelect])

  const onClickSetDates = useCallback(() => {
    showModal(<AITripDatePicker initStartDate={initStartDate} initEndDate={initEndDate} onSetDates={handleSetDates} />)
  }, [handleSetDates, initEndDate, initStartDate, showModal])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id && onLocationsReorder) {
      const oldIndex = locations.findIndex((location) => location.locationName === active.id)
      const newIndex = locations.findIndex((location) => location.locationName === over.id)

      const newLocations = arrayMove(locations, oldIndex, newIndex)
      onLocationsReorder(newLocations)
    }
  }, [locations, onLocationsReorder])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'trip_dates',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  return (
    <>
      {locations.length === 1 && <Group direction="vertical" gap={20} desktopGap={24}>
        <VerticalSpacer gap={8}>
          <BodyText variant="large" weight="bold">{stepNumber} of {totalSteps}</BodyText>
          <Heading variant="heading1">
            Trip <i>Dates</i>
          </Heading>
          <BodyText variant="large">
            When would you like to travel?
          </BodyText>
        </VerticalSpacer>
        <AITripDatePicker
          initStartDate={initStartDate}
          initEndDate={initEndDate}
          onSetDates={handleSetDates}
          locations={locations}
        />
      </Group>}
      {locations.length > 1 && <Group direction="vertical" gap={20} desktopGap={32}>
        <VerticalSpacer gap={8}>
          <BodyText variant="large" weight="bold">{stepNumber} of {totalSteps}</BodyText>
          <Heading variant="heading1">
            Trip <i>Dates</i>
          </Heading>
          <BodyText variant="large">
            When would you like to travel? You can update this anytime later.
          </BodyText>
        </VerticalSpacer>
        <VerticalSpacer gap={4}>
          <BodyText variant="large" weight="bold"> Trip dates </BodyText>
          <Clickable onClick={onClickSetDates}>
            <DateInput padding={12}>
              <Group direction="horizontal" gap={12}>
                <LineCalendarIcon />
                <BodyText variant="large">
                  {hasSelectedDates && initStartDate && initEndDate
                    ? formatCasualDateRange(initStartDate, initEndDate)
                    : 'Enter trip dates'}
                </BodyText>
              </Group>
            </DateInput>
          </Clickable>
        </VerticalSpacer>
        {hasSelectedDates && <VerticalSpacer gap={4}>
          <Group direction="vertical">
            <BodyText variant="large" weight="bold"> Set your destination order </BodyText>

            <BodyText variant="large" colour="neutral-five">
              You can adjust the order or number of days anytime later.
            </BodyText>
          </Group>
        </VerticalSpacer>}
        {hasSelectedDates && <VerticalSpacer gap={12}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={locations.map(location => location.locationName)}
              strategy={verticalListSortingStrategy}
            >
              {locations.map((location, index) => (
                <SortableAITripDateOrder
                  key={location.locationName}
                  id={location.locationName}
                  location={location}
                  locationIndex={index}
                  disabled={!onLocationsReorder || locations.length === 1}
                  onDurationChange={onLocationDurationChange}
                  onDelete={onLocationDelete}
                  hasTripDates={!!hasSelectedDates}
                />
              ))}
            </SortableContext>
          </DndContext>
        </VerticalSpacer>}
      </Group>}
    </>
  )
}

export default AITripDatesSelection
